import { useState } from 'react';
import type { FieldMetadata, ObjectMetadata } from '../../../data/client';
import { formatNumber } from '../../../fields/format';
import { Checkbox, IconButton, Popover, SegmentedControl, SelectMenu } from '../../../ui';
import type { RelationContext } from '../../filters/filterModel';
import type { ViewState } from '../views/viewState';
import { GroupControls } from './GroupControls';
import { groupableColumns } from './groupModel';
import { PAGE_SIZE_CHOICES } from './pageSize';
import { ColumnsSubPage, type TableControlProps } from './TableSheetControls';
import type { RecordSegment } from './TableToolbar';
import type { SortState } from './tableModel';
import { useTableControls } from './useTableControls';

/**
 * THE VIEW OPTIONS, AS ONE TOOLBAR ITEM (wave 4, C12; `spec/screens/list.md` section 2 item 3,
 * "the view options button (opens the sort, columns, group, density popover)", and item 10,
 * which anchors the kit's popover to that button).
 *
 * WHY THEY MOVED. Sort, Columns and Group stood in the list pane's own header row as three more
 * capsules, and with the detail pane open that row could not hold them: the view chip, Add
 * Filter and those three took 126 + 104 + 243 plus three gaps out of 611px, so the token strip's
 * window was narrower than one token's own minimum and a filter showed its field name and
 * nothing else (P4-W3-14, inventory C12). The spec had already put them in the toolbar. The row
 * keeps exactly what is about THIS LIST'S CONTENTS - which view, what narrows it, and the way to
 * narrow it further - and the three settings of the view's SHAPE stand in the window frame.
 *
 * ONE ITEM AND NOT THREE. A macOS toolbar item is a 36px glass shell with a monochrome symbol
 * and no label (kit: Titlebars and Toolbars/Light|Dark/XL/Buttons/Active/1, 36x36, whose only
 * text layer is the symbol; searching all 396 masters of that page for "label" returns nothing -
 * `crm-design/research/CRM-WAVE4-KIT-LOOKUP-2026-09-12.md` section 2), so three of them would be
 * three unlabelled symbols in a row for three settings of one thing. The popover is where the
 * three live, each under its own title.
 *
 * THE SYMBOL IS OPEN. `ui/Icon` carries no view-options mark and this round may not invent one -
 * a new glyph is a design step of its own, with the glyph tool and the owner's eye - so the
 * nearest existing symbol stands in: `sort`, the two arrows of different lengths, which is the
 * one mark in the set about the order and shape of a list. `/dev/list-states` carries the OPEN
 * caption with the search behind it.
 */
/** The pop-up's own "nothing chosen" value, the way `GroupControls` spells "Do Not Group". */
const NOT_SORTED = 'none';

/**
 * THE PANEL'S SECTION HEADING, AND IT IS NOT THE PHONE SHEET'S LABEL (wave 4 round 2,
 * finding 2).
 *
 * Round 1 drew these titles with `SHEET_LABEL` - `uppercase tracking-widest` - which is the
 * product's micro-caps vocabulary and is the thing the owner rejected on the record (triage
 * R-B1, owner-24 "Irgendwie passt das farblich nicht", owner-25 "Auch hier ist das design
 * komisch format, die abstaende zeichen und alles"). The macOS kit ships no uppercase section
 * style at all: the header of a section inside a menu is `Menus/Light|Dark/Regular/Header`,
 * one text layer in SFPro-Bold at 13 with a NEGATIVE kern of 0.2 and the secondary grey
 * (#737373 in light, #8A8A8A in dark) - a bold word in mixed case, never a tracked-out
 * capital. `Sidebars/Light/Large/Header` says the same thing with its own "Section Heading"
 * specimen, SFPro-Bold 13 at #B2B2B2.
 * kit: Apple macOS 27 UI Kit.sketch / Menus/Light/Regular/Header and Menus/Dark/Regular/Header
 *
 * THE THREE VALUES AND WHERE EACH COMES FROM: 13px is `--text-2xs`, the product's own label
 * size and the master's 13; `font-bold` is `--weight-bold`, 700, which is SFPro-Bold; the
 * colour is the product's existing secondary-label role `--color-ink-muted`, which is the
 * role the master's grey plays and is what these headings already used. No tracking utility,
 * because the master's kern is the system face's own at 13 and the product states body
 * tracking once on `body`.
 */
const PANEL_HEADING = 'text-2xs font-bold text-ink-muted';

export interface ViewOptionsProps {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  readonly relations: RelationContext;
  readonly state: ViewState;
  readonly onStateChange: (next: (current: ViewState) => ViewState) => void;
  readonly segment: RecordSegment;
  readonly onSegmentChange: (next: RecordSegment) => void;
  readonly pageSize: number;
  readonly onPageSizeChange: (next: number) => void;
}

export function ViewOptions({
  object,
  fields,
  relations,
  state,
  onStateChange,
  segment,
  onSegmentChange,
  pageSize,
  onPageSizeChange,
}: ViewOptionsProps) {
  const { columns, shownColumns, sort, setSort, toggleColumn, setFilter, grouping } =
    useTableControls({ object, fields, state, onStateChange });
  const [open, setOpen] = useState(false);

  /**
   * SINGLE-SELECT CLOSES ON CHOICE, MULTI-SELECT STAYS OPEN (rulebook M8 and M9, spec item 10).
   * WHICH field sorts is the single choice, so picking one - or clearing it - closes the panel;
   * the direction control refines a choice already made and leaves it open, and the columns, the
   * archived switch and the grouping settings are multi-select and close nothing. Clicking
   * outside keeps every one of them: a popover is not a dialog and has nothing to cancel.
   */
  const onSortChange = (next: SortState | null) => {
    setSort(next);
    if (next === null || next.fieldId !== sort?.fieldId) setOpen(false);
  };

  const controls: TableControlProps = {
    objectId: object.id,
    relations,
    columns,
    shownFieldIds: shownColumns.map((column) => column.field.id),
    onToggleColumn: toggleColumn,
    sort,
    onSortChange,
    filter: state.filter,
    onFilterChange: setFilter,
    grouping,
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="end"
      /*
        THE NAME AND THE TAG ARE ONE STRING HERE, AND THAT IS A PRIMITIVE'S RULE RATHER THAN A
        CHOICE. The copy deck's R10 asks a tooltip to start with a verb and not repeat the
        control's name ("Sort, show or group the rows"), and `ui/IconButton` renders its
        `label` as BOTH the accessible name and the help tag (SH-A9, which is what put every
        icon-only button's tag at its control). Splitting the two means giving that primitive a
        second prop, which is a `ui/*` change this slice may not make; wrapping the button in a
        second `Tooltip` was tried and breaks the popover, because `Popover`'s `asChild` then
        hands the trigger's props to the tooltip instead of to the button. The row is named in
        the report as left for I2.
      */
      trigger={<IconButton icon="sort" label="View options" />}
    >
      <div className="flex flex-col gap-4">
        {/*
          SORT IS A POP-UP AND A DIRECTION, NOT A LIST OF EVERY FIELD (the walk's own finding
          on this round's first build). The phone's `SortSubPage` is a radio row per column,
          which on the Agencies object is twenty-five 44px rows: the panel came out 846px tall
          in a 900px window, Columns and Group were a scroll away, and that is the exact defect
          the sheet's own note records against the desktop dropdowns it replaced. A pop-up
          button is what the kit ships for "one of many" on a content surface, and it is the
          construction `GroupControls` beside it already uses for "Group by".
          kit: Apple macOS 27 UI Kit.sketch / Pop-up Buttons/Light|Dark/Content Area/3 Rg
        */}
        <section className="flex flex-col gap-2">
          {/* A HEADING PER SECTION, not a field label: the four titles are what the panel is,
              and a screen reader walking it by heading reaches all four. The pop-up under this
              one carries its own name. */}
          <h2 className={PANEL_HEADING}>Sort</h2>
          <SelectMenu
            ariaLabel="Sort"
            value={sort?.fieldId ?? NOT_SORTED}
            onValueChange={(value) =>
              onSortChange(
                value === NOT_SORTED
                  ? null
                  : { fieldId: value, direction: sort?.direction ?? 'asc' },
              )
            }
            options={[
              { value: NOT_SORTED, label: 'Do not sort' },
              ...columns.map((column) => ({ value: column.field.id, label: column.field.label })),
            ]}
          />
          {/* The direction only exists once a field does, which is the rule the grouping
              section beside it follows for everything below its own dimension. */}
          {sort !== null && (
            <SegmentedControl
              size="sm"
              value={sort.direction}
              onValueChange={(direction) => setSort({ ...sort, direction })}
              ariaLabel="Direction"
              segments={[
                { value: 'asc', label: 'Ascending' },
                { value: 'desc', label: 'Descending' },
              ]}
            />
          )}
        </section>

        <section className="flex flex-col gap-2">
          <h2 className={PANEL_HEADING}>Columns</h2>
          {/* THE ONE LIST IN THE PANEL, AND IT IS BOUNDED. A column per field is as long as the
              object is wide, so it scrolls inside its own box rather than pushing Group off the
              bottom of the panel - the same bound `GroupMenu` has always put around its body. */}
          <div className="max-h-[min(18rem,40vh)] overflow-y-auto overscroll-contain">
            <ColumnsSubPage {...controls} />
          </div>
        </section>

        {/*
          ABSENT ENTIRELY ON AN OBJECT WITH NOTHING GROUPABLE, which is the rule the header row's
          filter control already follows: a section that opens on "nothing here can be grouped"
          is a dead end with a title.

          ONE HEADING, AND THE POP-UP DIRECTLY UNDER IT (wave 4 round 2, finding 2). The section
          said "Group" and `GroupControls` said "Group by" one line further down, which is two
          headings over one control. The component keeps its own visible label everywhere else -
          the phone's Adjust sheet drills into a page and needs it - and drops it here, where
          the section already carries the word; the control's NAME is unchanged, so nothing that
          asks for it by name moves.
        */}
        {groupableColumns(columns).length > 0 && (
          <section className="flex flex-col gap-2">
            <h2 className={PANEL_HEADING}>Group</h2>
            <GroupControls
              columns={columns}
              shownColumns={shownColumns}
              grouping={grouping}
              labelDimension={false}
            />
          </section>
        )}

        {/*
          ROWS PER PAGE IS A SECTION AND NOT A TAIL OF THE COLUMNS (wave 4 round 2, finding 2).
          It sat inside the Columns section because the dropdown this panel replaced carried it
          as the last group of the columns MENU - an accident of the old control, not a fact
          about the setting. The spec calls the panel "the sort, columns, group, DENSITY
          popover" and names density as one of the four things it holds, so it takes the fourth
          title rather than borrowing another section's.
        */}
        <section className="flex flex-col gap-2">
          <h2 className={PANEL_HEADING}>Rows per page</h2>
          <SegmentedControl
            size="sm"
            value={String(pageSize)}
            onValueChange={(value) => {
              // Resolved against the offered list rather than parsed: the discipline
              // `useLocalChoice` uses for a stored value, so the number has one source.
              const chosen = PAGE_SIZE_CHOICES.find((choice) => String(choice) === value);
              if (chosen !== undefined) onPageSizeChange(chosen);
            }}
            ariaLabel="Rows per page"
            segments={PAGE_SIZE_CHOICES.map((choice) => ({
              value: String(choice),
              label: formatNumber(choice),
            }))}
          />
        </section>

        {/*
          SHOW ARCHIVED IS A SETTING OF THE LIST, NOT A COLUMN AND NOT A SECTION (wave 4 round 2,
          finding 2). It is one switch about WHICH RECORDS the list holds, so it takes no title
          of its own and sits last, under a hairline - which is how a menu separates a setting
          from the choices above it (kit: Apple macOS 27 UI Kit.sketch /
          Menus/Light/Regular/Separator, one 1px rectangle of #000000 at alpha 0.07, and the
          Dark twin #FFFFFF at 0.08; `--color-line-subtle` is that value already). It was the
          last entry of the Columns section, where it read as a column somebody could show.
        */}
        <div className="border-line-subtle border-t pt-3">
          <Checkbox
            checked={segment === 'archived'}
            onCheckedChange={(on) => onSegmentChange(on ? 'archived' : 'active')}
            label="Show archived"
          />
        </div>
      </div>
    </Popover>
  );
}
